import { Router } from 'next/router';
import { useEffect } from 'react';
import NProgress from 'nprogress';

import { useTheme } from '@mui/material';
import {
  RemoteRouteStatus,
  ROUTE_PROGRESS_BAR_ID,
  safeExecuteProgress,
  useRemoteRouteStore,
} from '@kc-console/context';

const RouteProgressBar = () => {
  const theme = useTheme();

  useEffect(() => {
    try {
      /**
       * Route Progress Bar 설정 및 이벤트 핸들러 등록
       *
       * 동작 방식:
       * 1. routeChangeStart: NProgress 시작 (중복 실행 방지)
       * 2. routeChangeComplete:
       *    - 단순 route 이동 → 즉시 완료
       *    - 모듈 로드 필요 → 스토어 상태 변경 대기
       * 3. routeChangeError: 스토어 상태 확인 후 완료 처리
       * 4. 스토어 상태 변경: 모듈 로드 완료 시 완료 처리
       *
       * 참고: 서비스 내부 redirect(/vpcs → /vpcs?project_id)로 인한
       * 중복 routeChangeStart는 NProgress.isStarted()로 방지
       */
      NProgress.configure({ parent: `#${ROUTE_PROGRESS_BAR_ID}`, showSpinner: false });

      /**
       * Route 변경 시작 핸들러
       * - NProgress.isStarted()로 중복 실행 방지
       * - 서비스 내부 redirect로 인한 2번째 routeChangeStart 무시
       */
      const handleRouteStart = () => {
        if (NProgress.isStarted()) return;
        safeExecuteProgress(() => NProgress.start());
      };

      /**
       * Route 완료 핸들러 (공통)
       * - NProgress 종료 처리
       */
      const handleRouteEnd = () => {
        if (!NProgress.isStarted()) return;
        safeExecuteProgress(() => NProgress.done(false));
      };

      /**
       * Route 변경 완료 핸들러
       * - 단순 route 이동: 스토어 상태가 idle이면 즉시 완료
       * - 모듈 로드 필요: 스토어 상태 변경을 기다림 (subscribe에서 처리)
       */
      const handleRouteComplete = () => {
        const currentState = useRemoteRouteStore.getState();
        if (currentState.status === RemoteRouteStatus.Idle) {
          // 단순 route 이동으로 판단하여 즉시 완료
          handleRouteEnd();
        }
        // 모듈 로드가 필요한 경우는 스토어 subscribe에서 처리됨
      };

      // Next.js Router 이벤트 등록
      Router.events.on('routeChangeStart', (url, { shallow }) => {
        if (shallow) return;
        handleRouteStart();
      });
      Router.events.on('routeChangeComplete', (url, { shallow }) => {
        if (shallow) return;
        handleRouteComplete();
      });
      Router.events.on('routeChangeError', (url, { shallow }) => {
        if (shallow) return;
        handleRouteComplete(); // 에러 시에도 스토어 상태 확인 후 완료 처리
      });

      // Remote Route Store 구독 (모듈 로드 완료 감지)
      const unsub = useRemoteRouteStore.subscribe(
        state => ({ status: state.status }),
        state => {
          if (state.status === RemoteRouteStatus.Idle) {
            handleRouteEnd();
          }
        },
      );

      return () => {
        Router.events.off('routeChangeStart', handleRouteStart);
        Router.events.off('routeChangeComplete', handleRouteComplete);
        Router.events.off('routeChangeError', handleRouteEnd);
        unsub();
      };
    } catch (error) {
      console.error('Error setting up route progress bar:', error);
      return undefined;
    }
  }, []);

  return (
    <>
      <style jsx global>
        {`
          /* Make clicks pass-through */
          #nprogress {
            pointer-events: none;
          }

          #nprogress .bar {
            background: #3365e9;

            position: fixed;
            z-index: ${theme.zIndex.navigationTop};
            top: 0;
            left: 0;

            width: 100%;
            height: 4px;
          }

          /* Fancy blur effect */
          #nprogress .peg {
            display: block;
            position: absolute;
            right: 0px;
            width: 100px;
            height: 100%;
            box-shadow:
              0 0 10px #29d,
              0 0 5px #29d;
            opacity: 1;

            -webkit-transform: rotate(3deg) translate(0px, -4px);
            -ms-transform: rotate(3deg) translate(0px, -4px);
            transform: rotate(3deg) translate(0px, -4px);
          }

          /* Remove these to get rid of the spinner */
          #nprogress .spinner {
            display: block;
            position: fixed;
            z-index: ${theme.zIndex.navigationTop};
            top: 15px;
            right: 15px;
          }

          #nprogress .spinner-icon {
            width: 18px;
            height: 18px;
            box-sizing: border-box;

            border: solid 2px transparent;
            border-top-color: #29d;
            border-left-color: #29d;
            border-radius: 50%;

            -webkit-animation: nprogress-spinner 400ms linear infinite;
            animation: nprogress-spinner 400ms linear infinite;
          }

          .nprogress-custom-parent {
            overflow: hidden;
            position: relative;
          }

          .nprogress-custom-parent #nprogress .spinner,
          .nprogress-custom-parent #nprogress .bar {
            position: absolute;
          }

          @-webkit-keyframes nprogress-spinner {
            0% {
              -webkit-transform: rotate(0deg);
            }
            100% {
              -webkit-transform: rotate(360deg);
            }
          }
          @keyframes nprogress-spinner {
            0% {
              transform: rotate(0deg);
            }
            100% {
              transform: rotate(360deg);
            }
          }
        `}
      </style>
    </>
  );
};

export default RouteProgressBar;
